/**
 * Hybrid Drawer component.
 * Automatically chooses between the Tailwind shell (preferred) and the
 * styled-components shell based on props, mirroring `ButtonHybrid`. When a
 * consumer passes styled-system props (`px`, `color`, `height`, …) the styled
 * path is used so existing styling keeps working; otherwise the Tailwind path
 * renders.
 */
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";
import DrawerStyled from "./DrawerStyled";
import DrawerTailwind from "./DrawerTailwind";
import { DrawerHeader, DrawerContent, DrawerCloseButton } from "./DrawerShared";
import type { TypeDrawerProps } from "./DrawerTypes";

// Props the Drawer owns semantically. They must be stripped before the
// `hasStyledProps` check so a Drawer-specific prop never trips the styled
// router — most importantly `width`, which `hasStyledProps` also treats as a
// styled-system prop. Anything left in `rest` is a true pass-through/system
// prop and is what the routing decision is based on.
const DRAWER_OWN_PROPS: readonly string[] = [
  "children",
  "closeButtonLabel",
  "direction",
  "disableCloseOnClickOutside",
  "id",
  "modal",
  "open",
  "offset",
  "onClose",
  "zIndex",
  "width",
  "snapPoints",
  "defaultSnapPoint",
  "snapPoint",
  "onSnapPointChange",
  "snapToSequentialPoints",
  "actions",
  "actionsInHeader",
];

const DrawerHybrid = (props: TypeDrawerProps) => {
  const rest: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if (!DRAWER_OWN_PROPS.includes(key)) {
      rest[key] = (props as unknown as Record<string, unknown>)[key];
    }
  }

  if (hasStyledProps(rest)) {
    return <DrawerStyled {...props} />;
  }

  // Use the Tailwind version (preferred - better performance).
  return <DrawerTailwind {...props} />;
};

DrawerHybrid.Header = DrawerHeader;
DrawerHybrid.Content = DrawerContent;
DrawerHybrid.CloseButton = DrawerCloseButton;

export default DrawerHybrid;
